import type { Addresses } from 'api/types/addresses.types';
import type { ServiceLine } from 'api/types/service.types';
import type { TimeSlots } from 'api/types/timeSlot.types';
import { EditNew } from 'assets/icons';
import { generateAddressValue } from 'modes/standalone/addressModule/convertCareemAddress';
import { selectCurrentOrder, selectSelectedServices, selectServiceLineDropOffs } from 'pages/newOrder/newOrder.selectors';
import { useGetAllCustomerAddresses } from 'pages/queries/address.queries';
import React, { useCallback, useMemo } from 'react';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectIsFirstTimeCustomer } from 'redux-stores/slices/authSlice';
import { styled } from 'styled-components';

const StyledRow = styled(Row)``;

const StyledText = styled.p`
  color: ${({ theme }) => theme.colors.steelBlue};
  font-size: 13px;
  font-style: normal;
  font-weight: 300;
  line-height: 18px; /* 138.462% */
  letter-spacing: 0.2px;
  margin-bottom: 5px;
`;

const HeadingRow = styled(Row)`
  justify-content: space-between;
  margin-bottom: 16px;
`;

const StyledH2 = styled.h2`
  color: ${({ theme }) => theme.text.color};
  font-weight: 600;
  font-size: 16px;
  line-height: 21px;
  margin: 0;
  text-wrap: nowrap;
`;

const StyledEditNew = styled(EditNew)`
  > path {
    fill: ${({ theme }) => theme.components.optionItem.rightIcon.color};
  }
`;

interface Props {}

const OrderSummary: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { addressId, pickupTimeslot } = useSelector(selectCurrentOrder);
  const selectedServices = useSelector(selectSelectedServices);
  const selectedServiceLineDropOffs = useSelector(selectServiceLineDropOffs);
  const navigate = useNavigate();
  const isFirstTime = useSelector(selectIsFirstTimeCustomer);

  const { data: addresses } = useGetAllCustomerAddresses({
    enabled: Boolean(!addressId),
  });

  const selectedAddress = useMemo(() => addresses?.find((address: Addresses.Address) => address.id === addressId), [addressId, addresses]);

  const OrderSummaryInfo = useMemo(() => {
    const dropOffInfo = (serviceLine: ServiceLine, dropoffTimeSlot?: TimeSlots.Timeslot) => {
      if (!dropoffTimeSlot?.selectedDateTimeText) return null;
      return (
        <StyledText key={`${serviceLine}-${dropoffTimeSlot?.selectedDateTimeText}`}>
          {t(`homePage.${serviceLine}DeliveryInstructionHeading`)} {dropoffTimeSlot?.selectedDateTimeText}
        </StyledText>
      );
    };

    return (
      <>
        <StyledText>{generateAddressValue(selectedAddress)}</StyledText>
        <StyledText>
          {t('common.pickUp')} {pickupTimeslot?.selectedDateTimeText}
        </StyledText>
        {selectedServices.map((serviceLine: ServiceLine) => dropOffInfo(serviceLine, selectedServiceLineDropOffs[serviceLine]!!))}
      </>
    );
  }, [pickupTimeslot?.selectedDateTimeText, selectedAddress, selectedServiceLineDropOffs, selectedServices, t]);

  const onEditInfo = useCallback(() => {
    navigate(isFirstTime ? -1 : -2);
  }, [navigate, isFirstTime]);

  return (
    <StyledRow noGutters className="mb-5 align-items-center justify-content-between mt-2">
      <HeadingRow noGutters className="w-100">
        <StyledH2>{t('common.orderSummary')}</StyledH2>
        <StyledEditNew onClick={onEditInfo} />
      </HeadingRow>
      {OrderSummaryInfo}
    </StyledRow>
  );
};

export default OrderSummary;
